import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const BASE = '/api/it-tickets';

// Categories, statuses and whether this user works the queue. The isAgent flag
// comes from the server rather than a group check in the client: the agent gate
// is an env-configured Entra group the front end has no business knowing about.
export function useTicketMeta() {
  return useQuery({
    queryKey: ['itTickets', 'meta'],
    queryFn: async () => (await api.get(`${BASE}/meta`)).data,
    staleTime: 300000,
  });
}

// The people an agent can assign a ticket to (agents + handlers). Agents only;
// enabled is passed so a non-agent never fires the request (it would 403).
export function useAssignableUsers(enabled) {
  return useQuery({
    enabled: Boolean(enabled),
    queryKey: ['itTickets', 'assignable'],
    queryFn: async () => (await api.get(`${BASE}/assignable`)).data.users,
    staleTime: 300000,
  });
}

export function useMyTickets() {
  return useQuery({
    queryKey: ['itTickets', 'mine'],
    queryFn: async () => (await api.get(`${BASE}/mine`)).data.tickets,
    staleTime: 0,
  });
}

export function useTicketQueue(params = {}) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
  return useQuery({
    queryKey: ['itTickets', 'queue', clean],
    queryFn: async () => (await api.get(`${BASE}?${new URLSearchParams(clean)}`)).data.tickets,
    staleTime: 0,
  });
}

// Agents only (403 for anyone else), so the caller says when to fetch.
export function useTicketStats(enabled = true) {
  return useQuery({
    enabled: Boolean(enabled),
    queryKey: ['itTickets', 'stats'],
    queryFn: async () => (await api.get(`${BASE}/stats`)).data,
    staleTime: 0,
  });
}

export function useTicket(id) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['itTickets', 'one', String(id)],
    queryFn: async () => (await api.get(`${BASE}/${id}`)).data,
    staleTime: 0,
  });
}

// Every mutation invalidates the whole itTickets tree: a reply can reopen a
// resolved ticket, which changes the queue, the counts and my own list at once.
function useTicketMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['itTickets'] }),
  });
}

export const useRaiseTicket = () => useTicketMutation(
  async (body) => (await api.post(BASE, body)).data);

export const useReplyToTicket = () => useTicketMutation(
  async ({ id, ...body }) => (await api.post(`${BASE}/${id}/messages`, body)).data);

export const useUpdateTicket = () => useTicketMutation(
  async ({ id, ...patch }) => (await api.patch(`${BASE}/${id}`, patch)).data);

export const useAttachToTicket = () => useTicketMutation(
  async ({ id, file, name }) => (await api.post(`${BASE}/${id}/attachments`, { file, name })).data);

// Fetched only when opened — attachments come back as data URLs and pulling
// every one on list render would be wasteful.
export function useAttachment(attachmentId, enabled) {
  return useQuery({
    enabled: Boolean(attachmentId && enabled),
    queryKey: ['itTickets', 'attachment', String(attachmentId)],
    queryFn: async () => (await api.get(`${BASE}/attachments/${attachmentId}`)).data,
    staleTime: 600000,
  });
}
